import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { UploadCloud, Trash2, ArrowUp, ArrowDown, ImageOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const img = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);
const MAX = 15 * 1024 * 1024; // 15 MB

export const PhotoManager = ({ scope = 'site', eventId = '', title, hint }) => {
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]); // { name, progress, status }
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { scope };
      if (scope === 'event') params.event_id = eventId;
      const r = await api.get('/landing/photos', { params });
      setPhotos(r.data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [scope, eventId]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const tooBig = list.filter((f) => f.size > MAX);
    const valid = list.filter((f) => f.size <= MAX);
    if (tooBig.length) toast.error(`${tooBig.length} image${tooBig.length !== 1 ? 's' : ''} over the 15 MB limit ${tooBig.length !== 1 ? 'were' : 'was'} skipped.`);
    if (valid.length === 0) return;

    setUploading(true);
    setUploads(valid.map((f) => ({ name: f.name, progress: 0, status: 'uploading' })));

    let ok = 0;
    let failed = 0;
    for (let i = 0; i < valid.length; i += 1) {
      const f = valid[i];
      const form = new FormData();
      form.append('file', f);
      form.append('scope', scope);
      if (scope === 'event') form.append('event_id', eventId);
      try {
        await api.post('/landing/photos', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
            setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, progress: pct } : u)));
          },
        });
        ok += 1;
        setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, progress: 100, status: 'done' } : u)));
      } catch (e) {
        failed += 1;
        setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, status: 'error' } : u)));
      }
    }

    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} photo${ok !== 1 ? 's' : ''}`);
    if (failed) toast.error(`${failed} upload${failed !== 1 ? 's' : ''} failed. Please try again.`);
    await load();
    // Clear the progress list shortly after completion
    setTimeout(() => setUploads([]), 1500);
  };

  const setCaption = (id, caption) => setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, caption } : p)));
  const saveCaption = async (id, caption) => { try { await api.put(`/landing/photos/${id}`, { caption }); } catch (e) { /* noop */ } };
  const remove = async (id) => { try { await api.delete(`/landing/photos/${id}`); setPhotos((ps) => ps.filter((p) => p.id !== id)); toast.success('Photo removed'); } catch (e) { toast.error('Could not remove'); } };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[idx], next[j]] = [next[j], next[idx]];
    setPhotos(next);
    try { await api.post('/landing/photos/reorder', { ids: next.map((p) => p.id) }); } catch (e) { /* noop */ }
  };

  return (
    <div data-testid={`photo-manager-${scope}`}>
      {title && <h3 className="font-display text-lg font-semibold mb-1">{title}</h3>}
      {hint && <p className="text-sm text-muted-foreground mb-4">{hint}</p>}

      <div
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (!uploading) onFiles(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed border-border transition-colors p-8 text-center mb-5 ${uploading ? 'opacity-80 cursor-default' : 'cursor-pointer hover:border-primary/50'}`}
        data-testid="photo-dropzone"
      >
        <div className="flex flex-col items-center gap-2">
          <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center"><UploadCloud className="h-5 w-5 text-primary" /></div>
          <p className="font-medium">Drop photos here or click to upload</p>
          <p className="text-sm text-muted-foreground">JPG or PNG · up to 15 MB each · auto-optimized</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="photo-file-input" />
      </div>

      {/* Per-file upload progress */}
      {uploads.length > 0 && (
        <div className="space-y-2 mb-5" data-testid="upload-progress-list">
          {uploads.map((u, i) => (
            <div key={`${u.name}-${i}`} className="rounded-xl border border-border bg-card p-3" data-testid="upload-progress-item">
              <div className="flex items-center gap-2 mb-1.5">
                {u.status === 'done' ? <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  : u.status === 'error' ? <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                    : <Loader2 className="h-4 w-4 text-primary animate-spin flex-shrink-0" />}
                <span className="text-sm font-medium truncate flex-1">{u.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {u.status === 'error' ? 'Failed' : u.status === 'done' ? 'Done' : `${u.progress}%`}
                </span>
              </div>
              <Progress value={u.status === 'error' ? 100 : u.progress} className={`h-1.5 ${u.status === 'error' ? '[&>div]:bg-destructive' : ''}`} />
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : photos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <ImageOff className="h-6 w-6 opacity-60" /> No photos yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {photos.map((p, idx) => (
            <div key={p.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid="photo-item">
              <div className="relative aspect-[4/3] bg-secondary">
                <img src={img(p.thumb_url || p.url)} alt={p.caption || ''} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute top-2 right-2 flex gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="h-7 w-7 rounded-lg bg-black/45 text-white flex items-center justify-center disabled:opacity-30" data-testid="photo-move-up"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === photos.length - 1} className="h-7 w-7 rounded-lg bg-black/45 text-white flex items-center justify-center disabled:opacity-30" data-testid="photo-move-down"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(p.id)} className="h-7 w-7 rounded-lg bg-black/45 text-white flex items-center justify-center hover:bg-destructive" data-testid="photo-delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="p-2">
                <Input value={p.caption || ''} onChange={(e) => setCaption(p.id, e.target.value)} onBlur={(e) => saveCaption(p.id, e.target.value)} placeholder="Add a caption" className="h-9 text-sm" data-testid="photo-caption-input" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
